import { Box, BoxProps, Flex, Text } from "@chakra-ui/react";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { calculateTimeLeft } from "../../utils/time";
import { useMemo } from "react";
import { useTimer } from "../../screens/reader/hooks/useTimer";

interface LabeledCountdownTimerProps extends BoxProps {
  targetTimestamp: number;
  labelFontSize?: string;
  labelColor?: string;
  showAllLabels?: boolean;
  background?: string;
}

export function LabeledCountdownTimer({
  targetTimestamp,
  labelFontSize,
  labelColor,
  showAllLabels = true,
  background = "#141414",
  ...boxProps
}: LabeledCountdownTimerProps) {
  const isSmallScreen = useIsSmallBreakpoint();

  const timer = useTimer();

  const paddingY = isSmallScreen ? "12px" : "16px";

  const countdownTime = useMemo(
    () => calculateTimeLeft(targetTimestamp),
    [timer],
  );

  return (
    <Flex
      borderRadius="16px"
      border="1px solid"
      borderColor="transparent.white.20"
      background={`linear-gradient(${background}, ${background}) padding-box, linear-gradient(to bottom, var(--chakra-colors-transparent-white-50), var(--chakra-colors-transparent-white-20)) border-box`}
      px={isSmallScreen ? "16px" : "36px"}
      alignItems="stretch"
      justifyContent="center"
      gap={isSmallScreen ? 2 : 4}
      {...boxProps}
    >
      <TimeBox
        label="d"
        value={countdownTime.days}
        fontSize={labelFontSize}
        labelColor={labelColor}
      />
      <Box
        width="1px"
        py={paddingY}
        background="linear-gradient(to bottom, var(--chakra-colors-transparent-white-50), var(--chakra-colors-transparent-white-20))"
      />
      <TimeBox
        label="h"
        value={countdownTime.hours}
        fontSize={labelFontSize}
        labelColor={labelColor}
      />
      <Box
        width="1px"
        py={paddingY}
        background="linear-gradient(to bottom, var(--chakra-colors-transparent-white-50), var(--chakra-colors-transparent-white-20))"
      />

      <TimeBox
        label="m"
        value={countdownTime.minutes}
        fontSize={labelFontSize}
        labelColor={labelColor}
      />
      {showAllLabels && (
        <>
          <Box
            width="1px"
            py={paddingY}
            background="linear-gradient(to bottom, var(--chakra-colors-transparent-white-50), var(--chakra-colors-transparent-white-20))"
          />
          <TimeBox
            label="s"
            value={countdownTime.seconds}
            fontSize={labelFontSize}
            labelColor={labelColor}
          />
        </>
      )}
    </Flex>
  );
}

interface TimeBoxProps extends BoxProps {
  label: string;
  labelColor?: string;
  value: string | undefined;
}

function TimeBox({ label, value, labelColor, ...boxProps }: TimeBoxProps) {
  const isSmallScreen = useIsSmallBreakpoint();

  // Pad single digits with leading zeros
  const formattedValue = value ? value.padStart(2, "0") : "00";

  const defaultFontSize = isSmallScreen ? "20px" : "24px";

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      fontWeight="400"
      {...boxProps} // Spread props first
      fontSize={boxProps.fontSize ? boxProps.fontSize : defaultFontSize}
    >
      <Text
        variant="monospaceTime"
        as="span"
        fontSize="inherit"
        fontWeight="inherit"
        color="dune.dune"
        mr="2px"
      >
        {formattedValue}
      </Text>
      <Text
        as="span"
        variant="monospaceTime"
        fontSize="inherit"
        fontWeight="inherit"
        color={labelColor ?? "dune.800"}
      >
        {label}
      </Text>
    </Flex>
  );
}
